import en_gb from "../../translations/en_gb";
import nl_nl from "../../translations/nl_nl";
import { NestedValue } from "../../types/nested-value";
import { Reactive } from "../reactive";
import { TranslationsFile } from "./translations-file";

export type AllJavascriptVariableCharacters =
| 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l' | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x' | 'y' | 'z' // lowercase letters
| 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J' | 'K' | 'L' | 'M' | 'N' | 'O' | 'P' | 'Q' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z' // uppercase letters
| '$' | '_' // special characters
| '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'; // digits

export type GetVariableType<T extends string, Default = string> = T extends `string${infer A}`
    ? string
    : T extends `number${infer A}`
    ? number
    : Default

export type ParseVariableName<T extends string, Prefix extends string = ''> = T extends `${AllJavascriptVariableCharacters}${infer B}`
    ? T extends `${infer A}${infer B}`
        ? `${Prefix}${A}${ParseVariableName<B>}`
        : `${Prefix}${T}`
    : ''

/** Whether or not to add the `amount` variable to variables */
export type AddAmount<T extends string> = T extends `${infer A}{${infer B}|${infer C}}${infer D}`
    ? B extends `$${infer E}`
        ? AddAmount<D>
        : true
    : false

/** Parses all variables in a translation string */
export type ParseVariables<T extends string> = (
    T extends `${infer A}$${infer B}`
        ? {
            [b in ParseVariableName<B>]: T extends `${infer C}{$${infer D}`
                ? D extends B
                    ? GetVariableType<B, number>
                    : GetVariableType<B, string>
                : GetVariableType<B, string>
        } & (
            B extends `${ParseVariableName<B>}${infer C}`
                ? ParseVariables<C>
                : never
        )
        : {}
) & (
    AddAmount<T> extends true
    ? {
        /** The amount used to determine singular/multiple forms within `{}`'s, without a defined variable for it. */
        amount: number
    }
    : {}
)

/** Parses a translation into the translation and its variables */
export type ParseTranslation<T extends string> = T extends `${infer A}$${infer B}`
    ? {
        translation: string,
        variables: ParseVariables<T>
    }
    : AddAmount<T> extends true
    ? {
        translation: string,
        variables: ParseVariables<T>
    }
    : {
        translation: string
    }


/** A nested key function type specifically for translations */
export type NestedTranslationsKey<T extends object, U extends string = "", Depth extends number[] = []> =
    Depth["length"] extends 25 // Prevent excessive recursion depth
        ? never
        : T extends { translation: any, variables?: any }
            ? U // Stop at `{ translation: any }`
            : {
                [K in keyof T & string]: T[K] extends object
                    ? NestedTranslationsKey<T[K], `${U}${U extends "" ? "" : "."}${K}`, [...Depth, 1]>
                    : never
            }[keyof T & string];

/** When a property is an evaluation of a translation value */
export type TranslationValue = () => string

export type Locale = 'nl_NL' | 'en_GB'

/** The translations module */
export default class Translations {
    /** The current translations instance */
    public static current = new Translations({
        'en_GB': en_gb,
        'nl_NL': nl_nl
    }, 'en_GB')

    /** Get the current translations */
    public get currentTranslationsFile(): typeof en_gb {
        global.executions.addLine({className: "Translations",functionName: "[get] currentTranslationsFile",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "[get] currentTranslationsFile"],fileName: "/shared/utils/translations/translations.ts",childLine: 99,line: "        return this.translations[this.locale] as any"});
        return this.translations[this.locale] as any
    }

    /** The events */
    public events = {
        /** Changed event */
        changed: new Reactive.Subject<void>()
    }

    /**
     * Sets up the translations module
     * @param current The current translations
     */
    constructor(private translations: Record<Locale, typeof en_gb>, private locale: Locale) {
        global.executions.addLine({className: "Translations",functionName: "[constructor]",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "[constructor]"],fileName: "/shared/utils/translations/translations.ts",childLine: 113,line: "        this.setLocale(locale)"});
        this.setLocale(locale)
    }

    /** Sets the locale */
    public setLocale(locale: Locale) {
        global.executions.addLine({className: "Translations",functionName: "setLocale",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "setLocale"],fileName: "/shared/utils/translations/translations.ts",childLine: 118,line: "        this.locale = locale"});
        this.locale = locale
        global.executions.addLine({className: "Translations",functionName: "setLocale",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "setLocale"],fileName: "/shared/utils/translations/translations.ts",childLine: 119,line: "        this.events.changed.next()"});
        this.events.changed.next()
    }

    /** Translates a key */
    public translate<U extends NestedTranslationsKey<typeof en_gb>>(
        ...args: NestedValue<typeof en_gb, U> extends { variables: infer Variables }
            ? [
                U,
                Variables
            ]
            : [
                U
            ]
    ): string {
        global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 133,line: "        const [key, variables] = args"});
        const [key, variables] = args
        global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 134,line: "        let translation = key?.split('.').reduce((obj: any, k) => obj?.[k], this.currentTranslationsFile)?.translation"});
        let translation = key?.split('.').reduce((obj: any, k) => obj?.[k], this.currentTranslationsFile)?.translation

        if (typeof translation !== "string") {
            global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 137,line: "            throw new Error(`Translation key '${key}' not found.`);"});
            throw new Error(`Translation key '${key}' not found.`);
        }

        global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 140,line: "        const _variables = variables as Record<string, any>"});
        const _variables = variables as Record<string, any>

        // Replace `{}` placeholders with variables
        if (variables) {
            global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 144,line: "            translation = translation.replace(/(?<!\\{)\\$([a-zA-Z$_0-9]+)/g, (_: any, varName: string) => {"});
            translation = translation.replace(/(?<!\{)\$([a-zA-Z$_0-9]+)/g, (_: any, varName: string) => {
                global.executions.addLine({className: "Translations",functionName: "[anonymous]",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate", "[anonymous]"],fileName: "/shared/utils/translations/translations.ts",childLine: 145,line: "                return _variables![varName] ?? `$${varName}`"});
                return _variables![varName] ?? `$${varName}`
            });
        }

        // Handle pluralization logic {singular|plural}
        global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 150,line: "        translation = translation.replace(/\\{(\\$[a-zA-Z$_]+[a-zA-Z0-9$_]*\\:)?([^|}]+)\\|([^}]+)\\}/g, (_: any, variable: string, singular: string, plural: string) => {"});
        translation = translation.replace(/\{(\$[a-zA-Z$_]+[a-zA-Z0-9$_]*\:)?([^|}]+)\|([^}]+)\}/g, (_: any, variable: string, singular: string, plural: string) => {
            global.executions.addLine({className: "Translations",functionName: "[anonymous]",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate", "[anonymous]"],fileName: "/shared/utils/translations/translations.ts",childLine: 151,line: "            const value = variable?.length"});
            const value = variable?.length
                ? _variables
                    ? typeof _variables[variable.replace(/\:$/g, '').replace(/^\$/g, '')] === 'number'
                        ? _variables[variable.replace(/\:$/g, '').replace(/^\$/g, '')]
                        : 1
                    : 1
                : _variables.amount || 1
            global.executions.addLine({className: "Translations",functionName: "[anonymous]",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate", "[anonymous]"],fileName: "/shared/utils/translations/translations.ts",childLine: 158,line: "            return value === 1 ? singular : plural"});
            return value === 1 ? singular : plural
        });

        global.executions.addLine({className: "Translations",functionName: "translate",hierarchy: ["/shared/utils/translations/translations.ts", "Translations", "translate"],fileName: "/shared/utils/translations/translations.ts",childLine: 161,line: "        return translation as unknown as never;"});
        return translation as unknown as never;
    }
}